import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Download, Handshake } from "lucide-react";
import { useTranslation } from "react-i18next";
import { HOLIDAY_IMAGES, B2B_PDF_URL } from "../../data/holidays";
import { Chapter, Reveal, EnquiryButton } from "./shared";

export function HolidayB2B() {
  const { t } = useTranslation();
  return (
    <section className="bg-stoneWarm py-24 lg:py-36 border-t border-forest/10" data-testid="holidays-b2b">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="bg-forest text-bone relative overflow-hidden">
          <div className="absolute inset-0 grain pointer-events-none" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 p-8 sm:p-12 lg:p-20">
            <div className="lg:col-span-7">
              <Chapter number={t("holidays.b2b.chapter")} label={t("holidays.b2b.eyebrow")} tone="dark" />
              <Reveal delay={0.1}>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-7xl leading-[1.02] mt-8 uppercase tracking-tight">
                  {t("holidays.b2b.title")}
                </h2>
                <p className="font-display italic text-2xl lg:text-3xl text-bone/80 mt-4">{t("holidays.b2b.subtitle")}</p>
              </Reveal>
              <Reveal delay={0.2} className="mt-8 space-y-4 text-base md:text-lg text-bone/80 leading-relaxed max-w-2xl">
                <p>{t("holidays.b2b.p1")}</p>
                <p>{t("holidays.b2b.p2")}</p>
                <p>{t("holidays.b2b.p3")}</p>
              </Reveal>
            </div>
            <div className="lg:col-span-5 flex flex-col justify-between gap-10">
              <Reveal delay={0.25} className="border-l-2 border-terracotta pl-6">
                <p className="font-display text-3xl lg:text-4xl leading-tight">{t("holidays.b2b.tag_a")}</p>
                <p className="font-display italic text-3xl lg:text-4xl leading-tight text-bone/85 mt-1">{t("holidays.b2b.tag_b")}</p>
              </Reveal>
              <Reveal delay={0.35} className="flex flex-col gap-3">
                <EnquiryButton variant="light" testid="b2b-cta-partner" pkg="trade">
                  <Handshake className="w-4 h-4" /> {t("holidays.b2b.cta")}
                </EnquiryButton>
                {B2B_PDF_URL && (
                  <a
                    href={B2B_PDF_URL}
                    download
                    target="_blank"
                    rel="noreferrer"
                    data-testid="b2b-cta-pdf"
                    className="btn-lift inline-flex items-center justify-center gap-3 px-8 py-4 min-h-[52px] text-xs uppercase tracking-wider-3 border border-bone/50 text-bone hover:bg-bone hover:text-forest"
                  >
                    <Download className="w-4 h-4" /> {t("holidays.b2b.cta_pdf")}
                  </a>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function HolidayFinalCTA() {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <section ref={ref} className="relative min-h-[80svh] flex items-center overflow-hidden bg-forest-deep text-bone" data-testid="holidays-final-cta">
      <motion.img
        src={HOLIDAY_IMAGES.final}
        alt={t("holidays.final.alt")}
        loading="lazy"
        decoding="async"
        style={{ y }}
        className="absolute inset-0 w-full h-[124%] -top-[12%] object-cover"
      />
      <div className="absolute inset-0 bg-forest-deep/60" />
      <div className="absolute inset-0 bg-gradient-to-t from-forest-deep via-transparent to-forest-deep/40" />
      <div className="absolute inset-0 grain pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12 py-28 lg:py-40 w-full">
        <motion.h2
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "0px 0px -120px 0px" }}
          className="font-display leading-[0.95] uppercase tracking-tight text-[11vw] sm:text-7xl lg:text-[7.5rem]"
        >
          <span className="block overflow-hidden">
            <motion.span variants={{ hidden: { y: "110%" }, show: { y: 0 } }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }} className="block">
              {t("holidays.final.title_a")}
            </motion.span>
          </span>
          <span className="block overflow-hidden">
            <motion.span variants={{ hidden: { y: "110%" }, show: { y: 0 } }} transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.12 }} className="block italic normal-case">
              {t("holidays.final.title_b")}
            </motion.span>
          </span>
        </motion.h2>
        <Reveal delay={0.3} className="mt-8 text-base md:text-xl text-bone/85 max-w-xl leading-relaxed">{t("holidays.final.text")}</Reveal>
        <Reveal delay={0.4} className="mt-10">
          <EnquiryButton variant="light" testid="final-cta-request">{t("holidays.final.cta")}</EnquiryButton>
        </Reveal>
      </div>
    </section>
  );
}
